import { type FC, type MouseEvent, useMemo } from 'react';

import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { AdlinksContainer } from '@vakantiesnl/components/src/molecules/AdlinksContainer';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import type { AdlinkValues } from '@vakantiesnl/services/src/gtm/adlinks';
import { type Search, type Adlinks as AdlinkTypes } from '@vakantiesnl/types';

type Props = {
	adlinks?: AdlinkTypes.Adlink[];
	/** Gallery is open */
	isGalleryOpen?: boolean;
	/** Image modal is open */
	isImageModalOpen?: boolean;
	/** Check if CTA is clicked */
	handleAdlinkCtaClick?: (clickEvent: MouseEvent<HTMLButtonElement>) => void;
	/** Acco data for tracking events */
	accommodation: Search.FullAccommodationItem;
	/** Tracking function for adlink click */
	onAdlinkClick: (adlinkValues: AdlinkValues) => void;
	/** Content of the Google Analytics cookie */
	googleAnalyticsCookie?: string;
	/** Wether the acco is saved in the favorites */
	isFavorite?: boolean;
	onFavoriteClick?: () => void;
};

export const NonBookableView: FC<Props> = ({
	accommodation,
	adlinks,
	isGalleryOpen,
	isImageModalOpen,
	handleAdlinkCtaClick,
	onAdlinkClick,
	googleAnalyticsCookie,
	isFavorite,
	onFavoriteClick,
}) => {
	const { title: accommodationTitle } = accommodation;

	const microCopies = useMicroCopyContext();
	const { isTabletOrIsDesktop } = useBreakpoints();

	const showAdlinks = useMemo(
		() =>
			!isImageModalOpen ||
			(isImageModalOpen && !isGalleryOpen) ||
			(isGalleryOpen && isTabletOrIsDesktop && adlinks && adlinks.length > 0),
		[adlinks, isGalleryOpen, isTabletOrIsDesktop, isImageModalOpen],
	);

	return !!adlinks?.length ? (
		showAdlinks ? (
			<AdlinksContainer
				adlinks={adlinks}
				onToggleClick={handleAdlinkCtaClick}
				accommodationName={accommodationTitle}
				toggleVariant="primary"
				titleToShow={
					<Typography variant="headingLg" as="h3">
						{microCopies['adlinks.heading']}
					</Typography>
				}
				onAdlinkClick={onAdlinkClick}
				googleAnalyticsCookie={googleAnalyticsCookie}
				isFavorite={isFavorite}
				onFavoriteClick={onFavoriteClick}
				showFavorite={true}
			/>
		) : (
			/** Showing only the adlinks CTA e.g. in image gallery desktop view */
			<Button
				onClick={handleAdlinkCtaClick}
				aria-label={microCopies['adlinks.showAll']}
				size={isTabletOrIsDesktop ? 'medium' : 'small'}
				data-qa="button-show-more"
				variant="primary"
			>
				{microCopies['adlinks.showAll']}
			</Button>
		)
	) : null;
};
